import { useSelector } from "@xstate/react";
import { useCartActor } from "./CartProvider";
import {
  Bundle,
  BundleId,
  ImageAsset,
  Product,
  ProductId,
  SpecialPricePeriod,
} from "@greeter/core";
import { addOutline, removeOutline } from "ionicons/icons";
import { IonIcon } from "@ionic/react";
import { useState } from "react";
import { summarizeBundleCartItem } from "./utils";
import { classNames, isType } from "@greeter/util";
import {
  Card,
  OpenClosedIndicator,
  QuantityControls,
} from "@greeter/matter-v2";
import { CartItem, CartItemBundle, CartItemBundleForm } from "./CartMachine";
import { formatMoney } from "@greeter/formatters";
import { bundleSumTotalPrice } from "../BundlePicker/BundlePickerV3/util";
import { BundleFormSummary } from "../BundleFormSummary";

import css from "./CartContent.module.scss";

function ReadMoreButton(props: {
  onClick?: () => void;
  open?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div
      onClick={props.onClick}
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        ...props.style,
      }}
    >
      <span
        style={{ justifySelf: "flex-end", color: "rgba(160, 160, 160, 1)" }}
      >
        Se indhold
      </span>
      <OpenClosedIndicator
        style={{
          margin: "unset",
          height: "unset",
          width: 30,
          color: "rgba(160, 160, 160, 1)",
        }}
        open={props.open}
      />
    </div>
  );
}

function BundleFormCartItemCard({
  cartItem,
  ...props
}: {
  cartItem: CartItemBundleForm;
  forDate?: Date;
  specialPricePeriods?: Map<ProductId, Array<SpecialPricePeriod>>;
  onIncrement?: () => void;
  onDecrement?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <Card.Content>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", gap: 5 }}>
            <img
              className="cover w-full"
              src={ImageAsset.findUriWithSizeOrDefault(
                cartItem.form.coverAsset,
                "1x1-w256"
              )}
              style={{ width: 50, aspectRatio: 1 / 1 }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                alignItems: "flex-start",
                justifyContent: "center",
              }}
            >
              <span style={{ textAlign: "left" }}>{cartItem.form.title}</span>
              <span
                style={{
                  color: "rgba(69, 211, 194, 1)",
                  fontSize: 14,
                }}
              >
                {formatMoney(
                  bundleSumTotalPrice(cartItem.form, {
                    specialPricePeriods:
                      props.specialPricePeriods?.get(cartItem.form.id) ?? [],
                    forDate: props.forDate,
                  }) * cartItem.quantity
                )}{" "}
                kr.
              </span>
            </div>
          </div>
          <QuantityControls>
            {/* TODO: Implement this
              <QuantityControls.EditButton /> */}
            <QuantityControls.MinusButton onClick={props.onDecrement} />
            <QuantityControls.Value value={cartItem.quantity} />
            <QuantityControls.PlusButton onClick={props.onIncrement} />
          </QuantityControls>
        </div>
        {!open && (
          <ReadMoreButton
            open={open}
            onClick={() => setOpen((p) => !p)}
            style={{ paddingTop: 10 }}
          />
        )}
      </Card.Content>
      {open && (
        <div
          style={{
            width: "100%",
            borderBottom: "1px solid rgba(51, 51, 51, 1)",
          }}
        />
      )}
      {open && (
        <Card.Content>
          <BundleFormSummary bundle={cartItem.form} />
          <ReadMoreButton open={open} onClick={() => setOpen((p) => !p)} />
        </Card.Content>
      )}
    </Card>
  );
}

function BundleCartItemCard({
  cartItem: ci,
  ...props
}: {
  cartItem: CartItemBundle;
  bundles?: Map<BundleId, Bundle>;
  products?: Map<ProductId, Product>;

  specialPricePeriods?: Map<ProductId, Array<SpecialPricePeriod>>;
  forDate?: Date;
  onIncrement?: () => void;
  onDecrement?: () => void;
}) {
  return (
    <div key={ci.id} className={css.CartLine}>
      <div className={css.CartLineThumb}>
        {ci.coverAsset && (
          <img
            className="cover w-full"
            src={ImageAsset.findUriWithSizeOrDefault(ci.coverAsset, "1x1-w256")}
          />
        )}
      </div>
      <div className={css.CartLineInfo}>
        <div className={css.CartLineTitle}>{ci.title}</div>
        {isType(ci, "bundle") && props.bundles?.get(ci.bundleId) && (
          <div className={css.CartLineDescription}>
            {summarizeBundleCartItem(ci, props.bundles?.get(ci.bundleId)!)
              .split("\n")
              .map((l, i) => (
                <p
                  key={i + l}
                  {...classNames(
                    css.CartLineDescriptionLine,
                    l.startsWith("  ") && css.Indented
                  )}
                >
                  {l}
                </p>
              ))}
          </div>
        )}
        <div className={css.CartLinePrice}>{formatMoney(ci.price)} kr.</div>
      </div>
      <div className={css.CartLineActions}>
        <div className={css.CartLineButton} onClick={props.onDecrement}>
          <IonIcon icon={removeOutline} />
        </div>
        <div className={css.CartLineQuantity}>{ci.quantity}</div>
        <div className={css.CartLineButton} onClick={props.onIncrement}>
          <IonIcon icon={addOutline} />
        </div>
      </div>
    </div>
  );
}

export type CartContentProps = {
  /**
   * Used to provide extra context info.
   * Will summarize each cart line if
   */
  bundles?: Map<BundleId, Bundle>;
  products?: Map<ProductId, Product>;

  specialPricePeriods?: Map<ProductId, Array<SpecialPricePeriod>>;
  forDate?: Date;
};
export function CartContent(props: CartContentProps) {
  const cart = useCartActor();
  const total = useSelector(cart, (s) => s.context.total);
  const cartItems = useSelector(cart, (s) => s.context.state.value);

  const anyItems = Array.from(cartItems.values()).length > 0;

  function increment(ci: CartItem) {
    return function () {
      cart.send({
        type: "change",
        data: { type: "inc", data: ci.id },
      });
    };
  }
  function decrement(ci: CartItem) {
    return function () {
      cart.send({
        type: "change",
        data: { type: "dec", data: ci.id },
      });
    };
  }

  return (
    <div className={css.CartContent}>
      {!anyItems && <div>Kurven er tom...</div>}
      <div className={css.CartLines}>
        {Array.from(cartItems.values()).map((ci) =>
          isType(ci, "bundle") ? (
            <BundleCartItemCard
              cartItem={ci}
              products={props.products}
              bundles={props.bundles}
              onDecrement={decrement(ci)}
              onIncrement={increment(ci)}
            />
          ) : (
            isType(ci, "bundle-form") && (
              <BundleFormCartItemCard
                cartItem={ci}
                forDate={props.forDate}
                specialPricePeriods={props.specialPricePeriods}
                onDecrement={decrement(ci)}
                onIncrement={increment(ci)}
              />
            )
          )
        )}
      </div>
      {anyItems && (
        <div className={css.CartSummary}>
          <span>Pris:</span>
          <span>{formatMoney(total)} kr.</span>
        </div>
      )}
    </div>
  );
}
